
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch,
  provide,
  reactive,
} from "vue";
import InvestmentTicketSize from "@/components/wizard/steps/InvestmentTicketSize.vue";
import Notifications from "@/components/wizard/steps/Notifications.vue";
import IndustrySectors from "@/components/wizard/steps/IndustrySectors.vue";
import { StepperComponent } from "@/assets/ts/components";
import * as Yup from "yup";
import { useForm } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { useStore } from "vuex";
import { Actions } from "@/store/enums/StoreEnums";
import objectPath from "object-path";
import { useRouter } from "vue-router";

interface IStep1 {
  currencyCode: string;
  minInvestment: string;
  maxInvestment: string;
}

interface IStep2 {
  notification: string;
}

interface CreateAccount extends IStep1, IStep2 {}

export default defineComponent({
  name: "investor-profile",
  components: {
    InvestmentTicketSize,
    Notifications,
    IndustrySectors,
  },
  setup() {
    const loading = ref<boolean>(false);
    const showSuccess = ref<boolean>(false);
    const store = useStore();
    let formDataTemp = ref<Record<string, unknown>>({});
    let roleId = 0;
    const roles = computed(() => {
      return store.getters.getRolesData;
    });
    const _stepperObj = ref<StepperComponent | null>(null);
    const verticalWizardRef = ref<HTMLElement | null>(null);
    const currentStepIndex = ref(0);
    const user = computed(() => store.getters.getUser);
    const investorProfile = computed(() => store.getters.investorProfileGetter);
    const doesInvestorProfileExist = computed(() => {
      if (investorProfile.value) return true;
      else return false;
    });
    watch(user, () => {
      if (!Object.keys(user).length) {
        getInvestorProfile();
      }
    });
    const getInvestorProfile = async () => {
      if (!investorProfile.value)
        try {
          await store.dispatch(Actions.GET_INVESTOR_PROFILE, {
            id: user.value.id,
          });
          formDataTemp.value = { ...investorProfile.value };
        } catch (err) {
          console.log(err);
        }
      else formDataTemp.value = { ...investorProfile.value };
    };
    getInvestorProfile();
    provide("investorProfile", investorProfile);
    const formData = ref<CreateAccount>({
      currencyCode: "",
      minInvestment: "",
      maxInvestment: "",
      notification: "",
    });

    onMounted(async () => {
      _stepperObj.value = StepperComponent.createInsance(
        verticalWizardRef.value as HTMLElement
      );
      try {
        if (!roles.value.length) {
          const response = await store.dispatch(Actions.GET_ROLES);
          if (response !== true) throw new Error();
          roleId = objectPath.get(
            roles.value.find((role) => role.name.toLowerCase() === "investor"),
            "id",
            0
          );
        } else
          roleId = objectPath.get(
            roles.value.find((role) => role.name.toLowerCase() === "investor"),
            "id",
            0
          );
      } catch (err) {
        console.log("error in fetching roles");
      }
      setCurrentPageBreadcrumbs("Investor Profile", []);
    });
    const createAccountSchema = [
      Yup.object({
        currencyCode: Yup.string().required().label("Currency"),
        minInvestment: Yup.string().required().label("Minimum Investment"),
        maxInvestment: Yup.string().required().label("Maximum Investment"),
      }),
      Yup.object({
        notification: Yup.string().required().label("Notification"),
      }),
    ];

    const currentSchema = computed(() => {
      return createAccountSchema[currentStepIndex.value];
    });

    const { resetForm, handleSubmit } = useForm<IStep1 | IStep2>({
      validationSchema: currentSchema,
    });

    const totalSteps = computed(() => {
      if (!_stepperObj.value) {
        return;
      }

      return _stepperObj.value.totatStepsNumber;
    });

    resetForm({
      values: {
        ...formData.value,
      },
    });

    const handleStep = handleSubmit((values) => {
      console.log({ values });
      formData.value = {
        ...formData.value,
        ...values,
      };

      currentStepIndex.value++;

      if (!_stepperObj.value) {
        return;
      }

      _stepperObj.value.goNext();
    });

    const previousStep = () => {
      if (!_stepperObj.value) {
        return;
      }

      currentStepIndex.value--;

      _stepperObj.value.goPrev();
    };

    const formSubmit = async () => {
      loading.value = true;
      try {
        const response = await store.dispatch(
          doesInvestorProfileExist.value
            ? Actions.UPDATE_INVESTOR_PROFILE
            : Actions.CREATE_INVESTOR_PROFILE,
          doesInvestorProfileExist.value
            ? { id: investorProfile.value.id, data: formDataTemp.value }
            : formDataTemp.value
        );
        if (response !== true) throw new Error();
        try {
          const response = await store.dispatch(Actions.ATTACH_ROLE, {
            id: roleId,
          });
          if (response !== true) throw new Error();
          await store.dispatch(Actions.AUTH_USER);
          loading.value = false;
          store.commit("setAlert", {
            message: "Success",
            subMessage: "Role is pending for approval",
            variant: "primary",
            duration: 4000,
            show: true,
          });
        } catch (err) {
          loading.value = false;
          const error = store.getters.getErrors;

          store.commit("setAlert", {
            message: "Error",
            subMessage: error,
            variant: "danger",
            duration: 4000,
            show: true,
          });
        }
        loading.value = false;
        store.commit("setAlert", {
          message: "Success",
          subMessage: `Investor Profile ${
            doesInvestorProfileExist.value ? "Updated" : "Created"
          }`,
          variant: "primary",
          duration: 4000,
          show: true,
        });
        showSuccess.value = true;
      } catch (err) {
        loading.value = false;
        store.commit("setAlert", {
          message: "Error",
          subMessage: `Investor Profile ${
            doesInvestorProfileExist.value ? "Updation" : "Creation"
          } Unsuccessful`,
          variant: "danger",
          duration: 4000,
          show: true,
        });
      }
    };

    return {
      showSuccess,
      loading,
      doesInvestorProfileExist,
      formDataTemp,
      verticalWizardRef,
      previousStep,
      handleStep,
      formSubmit,
      totalSteps,
      currentStepIndex,
    };
  },
});
